// PILOTO-1 (firma #79) — la plomería compartida de GRUPOS.
//
// Vive aquí y no dentro de una pantalla porque tres superficies distintas la
// necesitan —la de profesor, la tarjeta discreta de Ajustes y la página
// /join/CODIGO— y porque así se puede probar sin montar React.

import { API_BASE } from './api';
import { formatDateShort, formatTimeShort, parseDateish } from './dateFormat';

// LA LLAVE LOCAL DEL DEEP LINK WEB. Cuando alguien abre /join/CODIGO SIN sesión,
// el código se guarda aquí y la tarjeta de Ajustes lo ofrece pre-llenado después
// de que entre. Es una comodidad, no un mecanismo de seguridad: el servidor
// vuelve a validar el código en cada intento, y borrarla solo cuesta re-teclear
// seis caracteres.
export const PENDING_JOIN_KEY = 'sb_pending_join_code';

export function stashPendingJoinCode(code) {
  try {
    if (code) localStorage.setItem(PENDING_JOIN_KEY, String(code).slice(0, 400));
  } catch (e) { /* Safari privado: la comodidad se pierde, nada se rompe */ }
}

export function readPendingJoinCode() {
  try { return localStorage.getItem(PENDING_JOIN_KEY) || ''; } catch (e) { return ''; }
}

/** Suelta el código pendiente — y CON ÉL su descarte (PILOTO-1D).
 *
 * Los dos hechos son sobre el mismo código y dejan de significar algo a la vez:
 * quien llega aquí es un FINAL (se unió, ya era miembro, o el código resultó
 * muerto). Dejar el descarte vivo tras soltar el pendiente sólo serviría para
 * silenciar un ofrecimiento futuro de un código que ya no existe — y si el
 * profesor regenera, el código nuevo es otro y ese descarte no le aplicaba de
 * todos modos. Ver `descartarEmpujon` para el porqué del descarte persistente. */
export function clearPendingJoinCode() {
  const pendiente = readPendingJoinCode();
  try { localStorage.removeItem(PENDING_JOIN_KEY); } catch (e) { /* idem */ }
  if (pendiente) olvidarDescarteDe(pendiente);
}

/** UNIRSE ES EL FINAL MÁS FINAL DE TODOS: el pendiente y el descarte del código
 *  al que se acaba de entrar dejan de significar nada.
 *
 *  Existe además de `clearPendingJoinCode` porque el código con el que uno se
 *  une NO siempre es el pendiente: en la tarjeta de Ajustes el alumno puede
 *  teclear otro. Las tres superficies que unen (el empujón, la tarjeta y
 *  `/join/CODIGO`) llaman a ésta, así que ninguna se puede desviar de las otras. */
export function olvidarCodigoUnido(code) {
  clearPendingJoinCode();
  olvidarDescarteDe(code);
}

/** El link que el profesor copia y reparte. Sale de API_BASE, que en producción
 *  es la constante quemada `https://quizessa.com` (lib/api.js): así el link no
 *  puede salir apuntando a un preview. */
export function joinLinkFor(code) {
  return `${API_BASE}/join/${code}`;
}

/** Copiar al portapapeles con el MISMO respaldo que ya usa el visor de
 *  diagnóstico (DiagReadyChainPage): la API moderna primero, y si el navegador
 *  la niega —WebView sin permiso, http sin TLS— un textarea oculto.
 *  Devuelve true/false; jamás lanza. */
export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      return true;
    } catch (e2) { return false; }
  }
}

/** El plural honesto de la lista: "1 alumno" / "N alumnos", vía i18n.
 *
 * LA CLAVE VA CON SU NAMESPACE EXPLÍCITO (`dashboard:`) y no desnuda, porque
 * esta función la llaman pantallas con namespaces por defecto DISTINTOS: la de
 * profesor arranca en `dashboard`, pero la tarjeta de Ajustes arranca en
 * `settings` y ahí una clave desnuda se resolvería contra settings.json y
 * saldría el literal `groups.member_count` a la cara del usuario. Con el prefijo
 * explícito, las dos resuelven igual — `dashboard` está en la lista de
 * namespaces cargados (i18n.js), así que siempre está disponible. */
export function memberCountLabel(t, count) {
  return t('dashboard:groups.member_count', { count: Number(count) || 0 });
}

// t7-3 remate 8 (#591, C16, regla del cero): en las pantallas del profe (la lista de grupos y la
// pagina del grupo), cero alumnos se dice "Sin alumnos" / "No students", nunca "0 alumnos"; con
// 1 o mas, el conteo de siempre. `memberCountLabel` no cambia (la usa tambien el alumno).
export function conteoDeAlumnos(t, count) {
  return (Number(count) || 0) === 0 ? t('dashboard:groups.member_count_ninguno') : memberCountLabel(t, count);
}

// ══════════════════════════════════════════════════════════════════════════════
//  PILOTO-1B (firma #80)
// ══════════════════════════════════════════════════════════════════════════════

// LA MARCA DE «YA LE PREGUNTÉ EN ESTE ATERRIZAJE». Vive en `sessionStorage`:
// SOBREVIVE A UN RECARGAR pero muere con la pestaña. Lo que acota es UNA
// CONSULTA POR ATERRIZAJE — el cubo `group_join_user` (20/h) es la defensa del
// código de ingreso, no un presupuesto que convenga quemar en reintentos.
//
// ── RE-ANCLADO EN PILOTO-1D (firma #82) ──────────────────────────────────────
// ANTES este comentario decía que la marca ERA la promesa de la firma: «cerrar
//        sin unirse → el modal no vuelve a salir SOLO EN ESA SESIÓN», y que
//        `localStorage` «lo habría enterrado para siempre» como si eso fuera lo
//        indeseable.
// AHORA  enterrarlo para siempre es EXACTAMENTE lo que Carlos firmó, y de eso se
//        encarga el descarte persistente (más abajo), no esta marca. La marca se
//        queda con el trabajo que siempre hizo de verdad —una consulta por
//        aterrizaje— y deja de cargar con una promesa de producto que ya no es
//        suya. Las dos llaves conviven porque son dos hechos: «ya pregunté en
//        esta pestaña» y «este alumno cerró ESTE ofrecimiento, para siempre».
//
// Y NINGUNA DE LAS DOS BORRA EL CÓDIGO PENDIENTE: el código sigue vivo y la
// tarjeta de Ajustes sigue pre-llenada. Ése es el respaldo que hace barato
// cerrar el modal, y es de PILOTO-1B.
export const PENDING_JOIN_PROMPTED_KEY = 'sb_pending_join_prompted';

export function markPendingJoinPrompted() {
  try { sessionStorage.setItem(PENDING_JOIN_PROMPTED_KEY, '1'); }
  catch (e) { /* Safari privado: se pierde la marca, no se rompe nada */ }
}

export function wasPendingJoinPrompted() {
  try { return sessionStorage.getItem(PENDING_JOIN_PROMPTED_KEY) === '1'; }
  catch (e) { return false; }
}

/** SUELTA la marca. La usa el modal cuando la consulta falla por algo que NO es
 *  un veredicto sobre el código (red caída, 5xx, 429, sesión): esa corrida no
 *  llegó a preguntar nada, así que no puede consumir el único empujón de la
 *  sesión. Es la misma receta que el repo ya firmó en `useAutoSplitRestore`:
 *  **lo que hace final al guard es una corrida COMPLETA**; una que falla lo
 *  libera para que el siguiente montaje reintente. */
export function clearPendingJoinPrompted() {
  try { sessionStorage.removeItem(PENDING_JOIN_PROMPTED_KEY); }
  catch (e) { /* idem */ }
}

/** ¿La puerta de entrada de este grupo está cerrada?
 *
 * EL VEREDICTO LO DA EL SERVIDOR (`code_expired`, services/groups.py) y esto lo
 * respeta cuando viene. El respaldo por fecha existe para una sola cosa: una
 * carga vieja en memoria que todavía no trae el campo. La regla de la ausencia
 * es la misma de los dos lados —«si no puedo probar que sigue vigente, está
 * vencido»—, así que las dos capas se inclinan hacia cerrar la puerta.
 *
 * EL RELOJ DEL RESPALDO TAMBIÉN ES INYECTABLE (`ahoraMs`), por la misma razón
 * que en `codeExpiryLabel`: sin él, quien le pase un reloj fijo a la etiqueta
 * seguía chocando aquí contra el reloj de pared, y un fixture fechado ayer se
 * volvía «vencido» hoy. Sin argumento es `Date.now()`, o sea el camino de
 * producción —y los llamadores de las pantallas— quedan idénticos. */
export function isCodeExpired(group, transcurridoMs, ahoraMs) {
  if (!group) return true;
  // EL SERVIDOR PUEDE CERRAR, PERO NO PUEDE ABRIR PARA SIEMPRE. Su `code_expired`
  // es una FOTO del instante del fetch, y estas pantallas leen UNA vez al montar:
  // una pestaña que el profesor deja abierta toda la noche seguiría pintando el
  // código en naranja y con los botones de copiar vivos a la mañana siguiente —
  // y él repartiría por WhatsApp una llave que ya contesta 410.
  //
  // Por eso las dos capas SE SUMAN en vez de cortocircuitarse, y las dos se
  // inclinan hacia cerrar: vale «vencido» si lo dice el servidor O si el reloj ya
  // pasó la fecha. La ausencia de fecha también es «vencido», que es la misma
  // regla que `is_code_expired` aplica en el backend.
  if (group.code_expired === true) return true;

  // LA CAPA DEL RELOJ, y con QUÉ reloj. Si el servidor mandó `code_expires_in`
  // —cuántos segundos faltaban cuando respondió—, la cuenta se hace con el
  // TIEMPO TRANSCURRIDO desde entonces (hooks/useCodeExpiryTick), no contra la
  // fecha absoluta. Un intervalo es correcto aunque el reloj del aparato esté
  // días adelantado; una comparación contra una fecha, no. Sin esto, una PC de
  // escuela con la pila del BIOS muerta le pintaba «Vencido» al profesor sobre
  // un código vivo, con los botones de copiar apagados y sin salida.
  if (typeof group.code_expires_in === 'number') {
    return group.code_expires_in * 1000 <= (transcurridoMs || 0);
  }

  // Respaldo para una carga vieja en memoria que aún no traiga el campo: la
  // fecha contra el reloj local. Y la ausencia de las dos cosas es «vencido»,
  // que es la misma regla que aplica el backend.
  const vence = parseDateish(group.code_expires_at);
  if (!vence) return true;
  return vence.getTime() <= (ahoraMs === undefined ? Date.now() : ahoraMs);
}

/** EL RELOJ ES INYECTABLE (`ahora`, en milisegundos), igual que su gemela del
 *  backend (`is_code_expired(group, now=...)`). No es un lujo de diseño: sin él,
 *  un test de la frontera «hoy / mañana» tiene que fabricar su fixture contra el
 *  reloj de pared, y uno que use las 23:59 revienta si la corrida cae en ese
 *  minuto. Una frontera se mide con dos números fijos.
 *
 *  El código de pantalla NO lo pasa nunca: sin argumento es `Date.now()`, o sea
 *  el camino de producción queda byte-idéntico. */

/** El día de calendario LOCAL de una marca, como número comparable. */
function diaLocal(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** «Vence hoy a las 7:40 p.m.» / «Vence mañana a las…» / «Vence el … a las …».
 *
 * Devuelve '' si el código ya venció (ahí la pantalla pinta la etiqueta
 * «Vencido», no una hora) o si no hay marca que pintar.
 *
 * HOY Y MAÑANA SE MIDEN EN DÍAS DE CALENDARIO DEL NAVEGADOR, no en «faltan menos
 * de 24 h»: a las 11 p.m. un código que vence a las 10 p.m. del día siguiente
 * está a 23 horas, y decirle «vence hoy» a alguien que ya está en la madrugada
 * sería mentirle. El tercer caso no debería ocurrir con un plazo de 24 h fijas;
 * existe para que un reloj desfasado —o un plazo distinto mañana— no produzca
 * una frase absurda en vez de una fecha. */
export function codeExpiryLabel(group, t, language, ahora) {
  // OJO CON LOS DOS RELOJES, que aquí conviven a propósito:
  //   · para DECIDIR si venció manda el servidor (`isCodeExpired`), porque esa
  //     es una verdad sobre su dato;
  //   · para PINTAR la hora manda el reloj y la zona del NAVEGADOR, porque «las
  //     7:40 p.m.» tiene que ser las 7:40 p.m. del profesor que lo lee.
  // `ahora` (ms) existe para que los tests midan la frontera hoy/mañana con dos
  // números fijos en vez de con el reloj de pared. Producción no lo pasa.
  const ahoraMs = ahora === undefined ? Date.now() : ahora;
  // Y el reloj VIAJA: `isCodeExpired` decide, pero su respaldo por fecha tiene
  // que medir contra el MISMO instante que la aritmética de días de abajo. Sin
  // pasárselo, la inyección quedaba a medias y un `ahora` de ayer chocaba
  // contra el reloj de pared de hoy. El segundo hueco es el TRANSCURRIDO, que
  // aquí no aplica (esta función no vive dentro del tic).
  if (!group || isCodeExpired(group, undefined, ahoraMs)) return '';
  const vence = parseDateish(group.code_expires_at);
  if (!vence) return '';
  const hora = formatTimeShort(vence, language);
  const dias = Math.round((diaLocal(vence) - diaLocal(new Date(ahoraMs))) / 86400000);
  if (dias <= 0) return t('dashboard:groups.expires_today', { time: hora });
  if (dias === 1) return t('dashboard:groups.expires_tomorrow', { time: hora });
  return t('dashboard:groups.expires_on', { date: formatDateShort(vence, language), time: hora });
}

/** «Se unió el 18 ago 2026 a las 9:30 a.m.» — la fecha que `group_members` ya
 *  guardaba desde PILOTO-1 y que hasta hoy nadie pintaba. Cadena vacía si la
 *  fila no la trae (una membresía vieja o un dato roto no rompe el renglón). */
export function joinedAtLabel(joinedAt, t, language) {
  const d = parseDateish(joinedAt);
  if (!d) return '';
  return t('dashboard:groups.joined_on', {
    date: formatDateShort(d, language),
    time: formatTimeShort(d, language),
  });
}

/** LAS CLAVES DEL NAMESPACE `dashboard:` SON EXPLÍCITAS AQUÍ TAMBIÉN, y por la
 *  misma razón que en `memberCountLabel`: estas funciones las llaman pantallas
 *  con namespaces por defecto distintos (la de profesor arranca en `dashboard`,
 *  el modal del chasis carga los suyos), y una clave desnuda le pintaría el
 *  literal al usuario en cuanto se use desde la equivocada. */

// ══════════════════════════════════════════════════════════════════════════════
//  PILOTO-1C (firma #81)
// ══════════════════════════════════════════════════════════════════════════════

// ── EL VUELO DEL EMPUJÓN, y por qué existe ────────────────────────────────────
//
// EL BUG QUE CIERRA (smoke de Carlos): el alumno que llega por `/join/CODIGO`,
// crea cuenta y verifica su correo aterriza SIN modal — aunque el código quedó
// bien guardado y Ajustes lo sigue ofreciendo pre-llenado. Tras un login normal
// sí sale.
//
// LA CAUSA, MEDIDA (no supuesta): el chasis (`AppLayout`) se monta UNA VEZ POR
// CARGA DE PÁGINA. React Router reconcilia por tipo y posición, así que cambiar
// de pantalla NO lo re-monta — verificado con un arnés de router real. El modal
// de PILOTO-1B, en cambio, está escrito sobre dos creencias contrarias:
//
//   1. Su propio docblock decía «AppLayout se re-monta en cada cambio de
//      pantalla». Es falso.
//   2. Su manejo de fallos suelta la marca «para que el siguiente montaje
//      reintente». Ese siguiente montaje NO EXISTE dentro de una carga: soltar
//      la marca no compraba nada hasta un F5.
//
// Consecuencia: hay UN solo intento por carga de página, y CUALQUIER final que
// no pinte el modal —la consulta muere en vuelo con el chasis, o falla por algo
// que no es veredicto— se lleva el empujón entero de esa carga, en silencio y
// con el código todavía pendiente. Que es exactamente el síntoma reportado; y
// explica por qué «tras login sí funciona»: entrar por `/login` (que vive FUERA
// del chasis) es lo único que produce un montaje nuevo.
//
// EL ARREGLO SON DOS PIEZAS, y las dos viven aquí para poder probarse sin React:
//
//   · EL VUELO. La consulta de esta carga se guarda en memoria de módulo. Un
//     montaje que encuentra un vuelo del MISMO código lo ADOPTA en vez de
//     preguntar otra vez: si murió en vuelo, la respuesta ya no se pierde, y la
//     doble corrida de StrictMode deja de necesitar un truco (la segunda adopta
//     la promesa de la primera, así que sigue siendo UNA sola petición).
//   · EL DESCARTE. Cerrar con la X se anota aquí, no en el estado del
//     componente, para que adoptar un vuelo ya contestado no le repinte encima
//     al alumno un modal que él ya cerró. (PILOTO-1D lo mudó a `localStorage` y
//     lo puso por código: ver «EL DESCARTE PERSISTENTE», más abajo.)
//
// MEMORIA DE MÓDULO A PROPÓSITO PARA EL VUELO: muere con la CARGA de página (un
// F5 la borra), que es justo su alcance. Lo que tiene que sobrevivir a un F5
// —«ya le pregunté en este aterrizaje»— sigue viviendo en `sessionStorage`,
// donde PILOTO-1B lo puso.
//
// ── PILOTO-1D (firma #82): EL DESCARTE CAMBIÓ DE CASA ────────────────────────
// ANTES: `let descartadoEnEstaCarga = false` — una sola bandera de módulo, sin
//        código y sin persistencia. Alcance: la carga de página.
// AHORA: una LISTA de códigos descartados en `localStorage`, con espejo en
//        memoria (ver `descartarEmpujon`). Alcance: el navegador, para siempre.
// POR QUÉ: decisión firmada por Carlos tras el smoke. Cerrar con la X es
//        DEFINITIVO — ni al navegar, ni tras F5, ni en una pestaña nueva. El
//        respaldo que queda es el código pre-llenado en Ajustes, que sigue
//        intacto. Y la llave es el CÓDIGO, no un booleano global: si el profesor
//        regenera, el link nuevo trae código nuevo, el descarte viejo no aplica
//        y el modal vuelve a ofrecer. Eso es correcto — es una invitación nueva.
let vueloEnCurso = null;      // { code, promesa } de ESTA carga de página

/** La consulta ya viva para este código, o null. */
export function vueloDelEmpujon(code) {
  return vueloEnCurso && vueloEnCurso.code === code ? vueloEnCurso.promesa : null;
}

/** Registra la consulta de esta carga y la devuelve, para encadenar. */
export function abrirVueloDelEmpujon(code, promesa) {
  vueloEnCurso = { code, promesa };
  return promesa;
}

/** Cierra el vuelo: el siguiente montaje vuelve a preguntar. Lo usa el camino
 *  de fallo SIN veredicto — esa corrida no supo nada del código, así que no
 *  puede dejar una respuesta adoptable detrás. */
export function cerrarVueloDelEmpujon() {
  vueloEnCurso = null;
}

// ── EL DESCARTE PERSISTENTE (PILOTO-1D) ──────────────────────────────────────

// LA LLAVE, y por qué es UNA con una lista dentro en vez de una llave por
// código: `localStorage` no se limpia solo. Con `sb_join_dismissed:ABC234` por
// cada código descartado, un aparato de laboratorio por el que pasan treinta
// alumnos al año acumula treinta llaves que nadie vuelve a mirar y que el
// `logout` tendría que barrer recorriendo el almacén entero. Una sola llave con
// una lista acotada se lee, se escribe y se borra de un golpe.
export const PENDING_JOIN_DISMISSED_KEY = 'sb_pending_join_dismissed';

// EL TOPE, y por qué existe: la lista es un recuerdo, no un registro. Veinte
// códigos cubren con holgura la vida entera de un alumno del piloto (diez grupos
// es su tope real, `MAX_GROUPS_PER_MEMBER`) y ponen un techo duro al tamaño de
// lo que se guarda. Al pasarse, se olvida el descarte MÁS VIEJO — que es el que
// con más probabilidad ya no corresponde a ningún código vivo.
const MAX_DESCARTES = 20;

// EL ESPEJO EN MEMORIA, y no es redundancia: en Safari privado `localStorage`
// lanza al escribir, y sin este espejo un alumno que cierra el modal ahí lo
// vería reaparecer al navegar dentro de la misma carga —que es justo el bug que
// PILOTO-1C cerró—. Con él, lo peor que pasa en ese navegador es que el descarte
// no sobreviva a un F5; el comportamiento dentro de la carga queda igual que el
// de todos los demás.
let descartadosEnEstaCarga = new Set();

/** LA LLAVE DE UN DESCARTE: el código canónico.
 *
 * El mismo grupo llega escrito de tres formas —`/join/abc-234` guarda el
 * segmento crudo, el alumno teclea `abc234`, el profesor dicta `ABC 234`— y las
 * tres tienen que descartar lo mismo. Se canoniza SÓLO lo que después de quitar
 * el ruido mide exactamente seis: el token largo del link (que también abre) se
 * guarda tal cual, para que no acabe recortado a seis caracteres y chocando con
 * el código de otro grupo. */
function llaveDeDescarte(code) {
  const crudo = String(code || '').trim();
  const limpio = crudo.toUpperCase().replace(/[^A-Z0-9]/g, '');
  return limpio.length === JOIN_CODE_LENGTH ? limpio : crudo;
}

/** La lista guardada, saneada. Cualquier cosa que no sea un arreglo de cadenas
 *  —una llave pisada a mano, un JSON a medio escribir— se lee como lista vacía:
 *  el peor caso es volver a ofrecer una vez, nunca reventar el aterrizaje. */
function leerDescartes() {
  try {
    const crudo = localStorage.getItem(PENDING_JOIN_DISMISSED_KEY);
    if (!crudo) return [];
    const lista = JSON.parse(crudo);
    return Array.isArray(lista) ? lista.filter(x => typeof x === 'string') : [];
  } catch (e) { return []; }
}

/** «El alumno cerró el ofrecimiento de ESTE código, y es definitivo.»
 *
 * Lo llaman la X y «Ahora no», que son el mismo gesto: cerrar sin unirse. NO
 * borra el código pendiente — el respaldo pre-llenado de Ajustes es la promesa
 * de PILOTO-1B y esta tanda no la toca. */
export function descartarEmpujon(code) {
  const llave = llaveDeDescarte(code);
  if (!llave) return;
  descartadosEnEstaCarga.add(llave);
  const lista = leerDescartes();
  if (lista.includes(llave)) return;
  lista.push(llave);
  while (lista.length > MAX_DESCARTES) lista.shift();
  try { localStorage.setItem(PENDING_JOIN_DISMISSED_KEY, JSON.stringify(lista)); }
  catch (e) { /* Safari privado: queda el espejo en memoria de esta carga */ }
}

export function fueDescartadoElEmpujon(code) {
  const llave = llaveDeDescarte(code);
  if (!llave) return false;
  return descartadosEnEstaCarga.has(llave) || leerDescartes().includes(llave);
}

/** Olvida el descarte de UN código. Lo llaman los finales (`clearPendingJoinCode`
 *  y `olvidarCodigoUnido`): un código que ya se usó o que ya murió no necesita
 *  que nadie recuerde que alguna vez se cerró su modal. */
export function olvidarDescarteDe(code) {
  const llave = llaveDeDescarte(code);
  if (!llave) return;
  descartadosEnEstaCarga.delete(llave);
  const lista = leerDescartes();
  const quedan = lista.filter(c => c !== llave);
  if (quedan.length === lista.length) return;
  try {
    if (quedan.length) localStorage.setItem(PENDING_JOIN_DISMISSED_KEY, JSON.stringify(quedan));
    else localStorage.removeItem(PENDING_JOIN_DISMISSED_KEY);
  } catch (e) { /* idem */ }
}

/** OLVIDO TOTAL del empujón — código pendiente, turno, vuelo y descarte.
 *
 * Su llamador de producción es UNO SOLO: el `logout` del contexto de auth. En
 * una máquina compartida (el laboratorio de la escuela es el caso del piloto),
 * el código que dejó el alumno anterior NO puede seguir persiguiendo al
 * siguiente: es del NAVEGADOR y sobrevivía al cierre de sesión. Quedó declarado
 * como pendiente en reporte_piloto1b.md §7 y se cierra aquí.
 *
 * PILOTO-1D — Y AHORA TAMBIÉN EL DESCARTE PERSISTENTE, que es lo único de este
 * mecanismo que sobrevive al cierre de la pestaña. Sin esta línea, el alumno que
 * cerró un modal el lunes le habría silenciado la invitación al que se sienta en
 * esa máquina el martes: un rastro mío en un aparato que no es mío. «Máquinas
 * compartidas del laboratorio: nada mío queda en el aparato.»
 *
 * Y de paso es el reinicio honesto para los tests: la memoria de módulo de
 * arriba no se borra sola entre casos de un mismo archivo. */
export function olvidarEmpujonPendiente() {
  clearPendingJoinCode();
  clearPendingJoinPrompted();
  cerrarVueloDelEmpujon();
  descartadosEnEstaCarga = new Set();
  try { localStorage.removeItem(PENDING_JOIN_DISMISSED_KEY); } catch (e) { /* idem */ }
}

// ── EL CÓDIGO, TAL Y COMO SE TECLEA ───────────────────────────────────────────

/** Lo que el alumno escribe o pega → el código canónico EN CURSO de escritura.
 *
 * NO es `normalize_join_code` del backend y no quiere serlo: aquélla dictamina
 * («esto es un código o no lo es») y ésta acompaña la escritura. Sube a
 * mayúsculas, tira lo que no es del alfabeto —espacios, guiones, el punto que
 * mete el teclado del celular— y corta en seis, que es el largo real. Pegar
 * «ABC-123» deja «ABC123».
 *
 * EL CORTE EN SEIS TIENE UNA CONSECUENCIA DECLARADA: el token largo del link ya
 * no se puede teclear en este campo. Ninguna superficie lo emite —`joinLinkFor`
 * reparte el código de seis— así que hoy no hay quien lo escriba; queda anotado
 * en el reporte por si algún día alguien reparte un link con token. */
export const JOIN_CODE_LENGTH = 6;

export function normalizeCodeInput(raw) {
  return String(raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, JOIN_CODE_LENGTH);
}

// ── EL COPY DE UN FALLO EN LAS SUPERFICIES DE GRUPOS ──────────────────────────

// EL SELLO DE SESIÓN, EN CRISTIANO. Síntoma del smoke: «Invalid CSRF token», en
// inglés crudo, a un profesor que solo tenía dos pestañas abiertas y entró con
// otra cuenta en una de ellas. El mecanismo NO se toca —el chequeo compartido
// vive en `dependencies.py` y es el candado de todas las mutaciones del
// producto—; lo que cambia es cómo se PINTA su rechazo.
//
// SE TRADUCE POR CÓDIGO DE ESTADO, que es la lección firmada en la tanda L: el
// `detail` crudo es la red de abajo, no la primera opción. Un 403 en una
// MUTACIÓN de grupos (unirse, crear, regenerar, renombrar, quitar, salir) es el
// rechazo del sello: las otras dos fuentes de 403 del backend —sin bandera
// institucional, o guest— no llegan a estas superficies, porque ninguna se le
// pinta a esas cuentas.
export const ESTADO_SELLO_DE_SESION = 403;

/** El texto que se le muestra al usuario cuando una acción de grupos falla.
 *
 * `porEstado` mapea códigos a claves propias de `dashboard:groups.*` (el 410 y
 * el 404 de las superficies de alumno, por ejemplo). Lo que no tenga copy
 * propio cae al `detail` del servidor, y sólo si es CADENA: algunos endpoints
 * del producto devuelven `detail` como OBJETO estructurado, y un objeto metido
 * en un toast o en el árbol de React revienta la pantalla. */
export function copyDeFalloDeGrupo(err, t, claveGenerica, porEstado = {}) {
  const status = err?.response?.status;
  if (status === ESTADO_SELLO_DE_SESION) return t('dashboard:groups.session_changed');
  const propia = porEstado[status];
  if (propia) return t(`dashboard:groups.${propia}`);
  const detail = err?.response?.data?.detail;
  return (typeof detail === 'string' && detail) ? detail : t(claveGenerica);
}
